import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const defaultColumns = {
  image: true,
  assetTag: true,
  description: true,
  brand: true,
  purchaseDate: true,
  cost: true,
  status: true,
  condition: true,
  purchasedFrom: true,
  model: true,
  serialNumber: true,
  site: true,
  location: true,
  category: true,
  department: true,
  assignedTo: true,
  createdAt: true,
  action: true,
};

const columnLabels = {
  image: 'Image',
  assetTag: 'Asset Tag ID',
  description: 'Description',
  brand: 'Brand',
  purchaseDate: 'Purchase Date',
  cost: 'Cost',
  status: 'Status',
  condition: 'Condition',
  purchasedFrom: 'Purchased From',
  model: 'Model',
  serialNumber: 'Serial Number',
  site: 'Site',
  location: 'Location',
  category: 'Category',
  department: 'Department',
  assignedTo: 'Assigned To',
  createdAt: 'Date Created',
  action: 'Action',
};

const ColumnSetupPage = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState(defaultColumns);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('assetTableColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        setColumns((prev) => ({ ...prev, ...parsed }));
      }
    } catch (error) {
      console.error('Error loading column settings:', error);
    }
  }, []);

  const { selectedCount, totalCount } = useMemo(() => {
    const vals = Object.values(columns);
    return {
      selectedCount: vals.filter(Boolean).length,
      totalCount: vals.length,
    };
  }, [columns]);

  const handleToggle = (key) => {
    setColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(columns).every(Boolean);
    const next = {};
    Object.keys(columns).forEach((k) => {
      next[k] = !allSelected;
    });
    setColumns(next);
  };

  const handleReset = () => {
    setColumns(defaultColumns);
  };

  const handleSave = () => {
    try {
      setSaving(true);
      localStorage.setItem('assetTableColumns', JSON.stringify(columns));
      toast.success('Column preferences saved for Assets table');
      navigate(-1);
    } catch (error) {
      console.error('Error saving column settings:', error);
      toast.error('Error saving column settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="column-setup-page" style={{ padding: '16px' }}>
      <div
        style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'linear-gradient(rgb(26, 32, 44) 0%, rgb(45, 55, 72) 100%)',
              color: 'white',
              borderRadius: '7px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '12px',
            }}
          >
            ← Back
          </button>
        </div>
        <div
          style={{
            marginBottom: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div>
            <div style={{ fontSize: '14px', color: '#718096' }}>
              Choose which columns are visible in the <strong>Assets</strong> table.
            </div>
            <div style={{ fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>
              These settings are saved in your browser and used on the Assets page.
            </div>
          </div>
          <div style={{ fontSize: '13px', color: '#4a5568', fontWeight: 500 }}>
            {selectedCount} of {totalCount} columns selected
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginBottom: '12px',
          }}
        >
          <button
            onClick={handleSelectAll}
            style={{
              padding: '6px 12px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
          </button>
          <button
            onClick={handleReset}
            style={{
              padding: '6px 12px',
              background: '#fed7d7',
              color: '#c53030',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            Reset to Default
          </button>
        </div>

        <div
          style={{
            maxHeight: '460px',
            overflowY: 'auto',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px',
            }}
          >
            {Object.entries(columnLabels).map(([key, label]) => (
              <label
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px',
                  background: columns[key] ? '#f0f9ff' : '#f7fafc',
                  border: `2px solid ${columns[key] ? '#667eea' : '#e2e8f0'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <input
                  type="checkbox"
                  checked={columns[key] || false}
                  onChange={() => handleToggle(key)}
                  style={{
                    marginRight: '10px',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#667eea',
                  }}
                />
                <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: 500 }}>{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            paddingTop: '16px',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              padding: '10px 20px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Columns'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnSetupPage;

